import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../api';

import { getUser } from '../App';

function CustomerDetail() {
  const staff = getUser();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromMessages = location.state?.fromMessages;
  const fromCustomerId = location.state?.fromCustomerId;
  const [customer, setCustomer] = useState(null);
  const [visits, setVisits] = useState([]);
  const [piercings, setPiercings] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [notes, setNotes] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [rescheduleBooking, setRescheduleBooking] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleSlots, setRescheduleSlots] = useState([]);
  const [rescheduleLoadingSlots, setRescheduleLoadingSlots] = useState(false);

  // New booking state
  const [studios, setStudios] = useState([]);
  const [showCreateBooking, setShowCreateBooking] = useState(false);
  const [createStudioId, setCreateStudioId] = useState('');
  const [createDate, setCreateDate] = useState('');
  const [createTime, setCreateTime] = useState('');
  const [createSlots, setCreateSlots] = useState([]);
  const [createLoadingSlots, setCreateLoadingSlots] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(true);

  // Edit mode state
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');

  const canEdit = staff && (staff.role === 'owner' || staff.role === 'manager');
  const [expandedVisits, setExpandedVisits] = useState(new Set());

  const toggleVisit = (visitId) => {
    setExpandedVisits(prev => {
      const next = new Set(prev);
      next.has(visitId) ? next.delete(visitId) : next.add(visitId);
      return next;
    });
  };

  const fetchCustomer = async () => {
    try {
      const res = await api.get(`/customers/${id}`);
      setCustomer(res.data.customer);
      setVisits(res.data.visits);
      setPiercings(res.data.piercings);
      setPurchases(res.data.purchases);
      setNotes(res.data.notes);
      setFollowUps(res.data.follow_ups);
    } catch (err) {
      console.error('Failed to load customer:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomer();
    fetchBookings();
    fetchStudios();
  }, [id]);

  const fetchStudios = async () => {
    try {
      const res = await api.get('/booking/studios');
      setStudios(res.data.studios || []);
    } catch (err) {
      console.error('Failed to load studios:', err);
    }
  };

  // Clear success message after 3 seconds
  useEffect(() => {
    if (editSuccess) {
      const t = setTimeout(() => setEditSuccess(''), 3000);
      return () => clearTimeout(t);
    }
  }, [editSuccess]);

  const startEditing = () => {
    setEditData({
      name: customer.name || '',
      phone: customer.phone || '',
      whatsapp_number: customer.whatsapp_number || '',
      email: customer.email || '',
      nationality: customer.nationality || '',
      birthday: customer.birthday || '',
      referral_source: customer.referral_source || ''
    });
    setEditError('');
    setEditSuccess('');
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditData({});
    setEditError('');
  };

  const handleEditChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!(editData.name || '').trim()) {
      setEditError('Name is required');
      return;
    }
    setEditSaving(true);
    setEditError('');
    try {
      await api.put(`/customers/${id}`, {
        name: (editData.name || '').trim(),
        phone: (editData.phone || '').trim() || null,
        whatsapp_number: (editData.whatsapp_number || '').trim() || null,
        email: (editData.email || '').trim() || null,
        nationality: (editData.nationality || '').trim() || null,
        birthday: (editData.birthday || '').trim() || null,
        referral_source: (editData.referral_source || '').trim() || null
      });
      setEditSuccess('Customer updated');
      setEditing(false);
      fetchCustomer();
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to update customer');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Permanently delete ${customer.name}? This cannot be undone.`)) return;
    if (!window.confirm(`Are you sure? All visits, piercings and messages for ${customer.name} will be deleted.`)) return;
    try {
      await api.delete(`/customers/${id}`);
      navigate('/customers');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete customer');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/customers/${id}/notes`, { note: newNote });
      setNewNote('');
      fetchCustomer();
    } catch (err) {
      console.error('Failed to add note:', err);
    }
  };

  const fetchBookings = async () => {
    try {
      const res = await api.get(`/booking/customer/${id}`);
      setBookings(res.data.bookings);
    } catch (err) {
      console.error('Failed to load bookings:', err);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Cancel this booking?')) return;
    setActionLoading(bookingId);
    try {
      await api.post(`/booking/${bookingId}/staff-cancel`);
      setBookings(prev => prev.filter(b => b.id !== bookingId));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to cancel booking');
    } finally {
      setActionLoading(null);
    }
  };

  const openReschedule = (booking) => {
    setRescheduleBooking(booking);
    setRescheduleDate('');
    setRescheduleTime('');
    setRescheduleSlots([]);
  };

  const handleRescheduleDateChange = async (date) => {
    setRescheduleDate(date);
    setRescheduleTime('');
    if (!date || !rescheduleBooking) return;
    setRescheduleLoadingSlots(true);
    try {
      const res = await api.get('/booking/slots', { params: { studio_id: rescheduleBooking.studio_id, date } });
      setRescheduleSlots((res.data.slots || []).filter(s => s.available));
    } catch (err) {
      setRescheduleSlots([]);
    } finally {
      setRescheduleLoadingSlots(false);
    }
  };

  const handleRescheduleConfirm = async () => {
    if (!rescheduleDate || !rescheduleTime || !rescheduleBooking) return;
    setActionLoading(rescheduleBooking.id);
    try {
      await api.post(`/booking/${rescheduleBooking.id}/reschedule`, { date: rescheduleDate, time: rescheduleTime });
      setRescheduleBooking(null);
      fetchBookings();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reschedule');
    } finally {
      setActionLoading(null);
    }
  };

  const openCreateBooking = () => {
    // Default studio to the one matching the customer's country, else first studio
    const match = studios.find(s => s.country === customer.country);
    setCreateStudioId(match ? match.id : (studios[0]?.id || ''));
    setCreateDate('');
    setCreateTime('');
    setCreateSlots([]);
    setCreateError('');
    setShowCreateBooking(true);
  };

  const loadCreateSlots = async (studioId, date) => {
    setCreateTime('');
    setCreateSlots([]);
    if (!studioId || !date) return;
    setCreateLoadingSlots(true);
    try {
      const res = await api.get('/booking/slots', { params: { studio_id: studioId, date } });
      setCreateSlots((res.data.slots || []).filter(s => s.available));
    } catch (err) {
      setCreateSlots([]);
    } finally {
      setCreateLoadingSlots(false);
    }
  };

  const handleCreateStudioChange = (studioId) => {
    setCreateStudioId(studioId);
    loadCreateSlots(studioId, createDate);
  };

  const handleCreateDateChange = (date) => {
    setCreateDate(date);
    loadCreateSlots(createStudioId, date);
  };

  const handleCreateBookingConfirm = async () => {
    if (!createStudioId || !createDate || !createTime) return;
    setCreateSubmitting(true);
    setCreateError('');
    try {
      await api.post('/booking/staff-create', {
        studio_id: createStudioId,
        customer_name: customer.name,
        customer_phone: customer.phone || customer.whatsapp_number || null,
        customer_email: customer.email || null,
        date: createDate,
        time: createTime,
        source: 'manual'
      });
      setShowCreateBooking(false);
      fetchBookings();
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to create booking');
    } finally {
      setCreateSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const visitTypeLabel = (type) => {
    const labels = {
      new_piercing: 'New Piercing',
      downsizing: 'Downsizing',
      checkup: 'Check-up',
      consultation: 'Consultation'
    };
    return labels[type] || type;
  };

  const checkupIssueLabel = (issue) => {
    const labels = {
      redness: 'Redness',
      swelling: 'Swelling',
      irritation: 'Irritation',
      jewelry_issue: 'Jewelry Issue',
      other: 'Other'
    };
    return labels[issue] || issue;
  };

  const placementLabelVisit = (placement) => {
    return (placement || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const visitTypeTag = (type) => {
    const classes = {
      new_piercing: 'tag-green',
      downsizing: 'tag-blue',
      checkup: 'tag-orange'
    };
    return classes[type] || 'tag-blue';
  };

  const placementLabel = (placement) => {
    return placement.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const followUpLabel = (trigger) => {
    const labels = {
      day7_healing: 'Day 7 Healing Check',
      day30_downsize: 'Day 30 Downsize Reminder',
      month6_nudge: '6 Month Nudge',
      birthday: 'Birthday Message',
      post_purchase: 'Post-Purchase Follow-up'
    };
    return labels[trigger] || trigger;
  };

  const statusTag = (status) => {
    const classes = {
      pending: 'tag-orange',
      sent: 'tag-green',
      skipped: 'tag-red'
    };
    return classes[status] || 'tag-blue';
  };

  if (loading) return <div className="loading">Loading customer...</div>;
  if (!customer) return <div className="loading">Customer not found</div>;

  return (
    <div>
      <div className="customer-header" style={{ flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/customers')}>
              ← Back to Customers
            </button>
            {fromMessages && (
              <button className="btn btn-secondary btn-sm"
                onClick={() => navigate('/messages', { state: { openCustomerId: fromCustomerId } })}>
                ← Back to Chat
              </button>
            )}
          </div>
          {staff?.role === 'owner' && (
            <button className="btn btn-sm" onClick={handleDelete}
              style={{ background: 'none', border: '1px solid #b32d2d', color: '#b32d2d', fontSize: '0.8rem' }}>
              Delete Profile
            </button>
          )}
        </div>
        <div>
          <h1>{customer.name}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <span className="tag tag-blue">{customer.country}</span>
            {customer.is_minor && (
              <span style={{ fontSize: '0.75rem', padding: '0.2rem 0.6rem', borderRadius: 8, background: '#f3eef8', color: '#6b4c8a', fontWeight: 600 }}>
                Minor
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Guardian card (shown on minor's profile) ── */}
      {customer.is_minor && customer.guardian_customer_id && (
        <div className="card" style={{ borderLeft: '4px solid #6b4c8a', marginBottom: 16 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b4c8a', marginBottom: 10 }}>
            Parent / Guardian
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '1rem', marginBottom: 4 }}>{customer.guardian_name}</div>
              {customer.guardian_phone && <div style={{ fontSize: '0.85rem', color: '#7A766F' }}>{customer.guardian_phone}</div>}
              {customer.guardian_email && <div style={{ fontSize: '0.85rem', color: '#7A766F' }}>{customer.guardian_email}</div>}
            </div>
            <button className="btn btn-sm btn-secondary"
              onClick={() => navigate(`/customers/${customer.guardian_customer_id}`)}>
              View Profile →
            </button>
          </div>
        </div>
      )}

      {/* ── Linked minors card (shown on guardian's profile) ── */}
      {customer.linked_minors?.length > 0 && (
        <div className="card" style={{ borderLeft: '4px solid #6b4c8a', marginBottom: 16 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b4c8a', marginBottom: 10 }}>
            Minor Profiles (Guardian)
          </div>
          {customer.linked_minors.map(minor => (
            <div key={minor.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{minor.name}</div>
                {minor.phone && <div style={{ fontSize: '0.8rem', color: '#7A766F' }}>{minor.phone}</div>}
              </div>
              <button className="btn btn-sm btn-secondary"
                onClick={() => navigate(`/customers/${minor.id}`)}>
                View Profile →
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Contact Information — with edit mode */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h3 style={{ margin: 0 }}>Contact Information</h3>
          {canEdit && !editing && (
            <button className="btn btn-sm btn-secondary" onClick={startEditing}>
              Edit
            </button>
          )}
        </div>

        {editError && <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}>{editError}</div>}
        {editSuccess && <div className="alert alert-success" style={{ marginBottom: '0.75rem' }}>{editSuccess}</div>}

        {editing ? (
          /* ===== EDIT MODE ===== */
          <div>
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={editData.name}
                onChange={e => handleEditChange('name', e.target.value)}
                placeholder="Full name"
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={editData.phone}
                  onChange={e => handleEditChange('phone', e.target.value)}
                  placeholder="+852 9123 4567"
                />
              </div>
              <div className="form-group">
                <label>WhatsApp</label>
                <input
                  type="tel"
                  value={editData.whatsapp_number}
                  onChange={e => handleEditChange('whatsapp_number', e.target.value)}
                  placeholder="+852 9123 4567"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={editData.email}
                onChange={e => handleEditChange('email', e.target.value)}
                placeholder="email@example.com"
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div className="form-group">
                <label>Nationality</label>
                <input
                  type="text"
                  value={editData.nationality}
                  onChange={e => handleEditChange('nationality', e.target.value)}
                  placeholder="e.g. Hong Kong"
                />
              </div>
              <div className="form-group">
                <label>Birthday</label>
                <input
                  type="date"
                  value={editData.birthday}
                  onChange={e => handleEditChange('birthday', e.target.value)}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Referral Source</label>
              <input
                type="text"
                value={editData.referral_source}
                onChange={e => handleEditChange('referral_source', e.target.value)}
                placeholder="e.g. Instagram, Walk-in, Friend"
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <button className="btn btn-primary" onClick={handleSave} disabled={editSaving}>
                {editSaving ? 'Saving...' : 'Save Changes'}
              </button>
              <button className="btn btn-secondary" onClick={cancelEditing} disabled={editSaving}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          /* ===== READ MODE (unchanged) ===== */
          <div className="customer-info-grid">
            <div className="info-item">
              <label>Phone</label>
              <span>{customer.phone || '—'}</span>
            </div>
            <div className="info-item">
              <label>WhatsApp</label>
              <span>{customer.whatsapp_number || '—'}</span>
            </div>
            <div className="info-item">
              <label>Email</label>
              <span>{customer.email || '—'}</span>
            </div>
            <div className="info-item">
              <label>Nationality</label>
              <span>{customer.nationality || '—'}</span>
            </div>
            <div className="info-item">
              <label>Birthday</label>
              <span>{formatDate(customer.birthday)}</span>
            </div>
            <div className="info-item">
              <label>Referral Source</label>
              <span>{customer.referral_source || '—'}</span>
            </div>
            <div className="info-item">
              <label>Customer Since</label>
              <span>{formatDate(customer.created_at)}</span>
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h3 style={{ margin: 0 }}>Upcoming Bookings ({bookings.length})</h3>
          {!showCreateBooking && (
            <button className="btn btn-sm btn-primary" onClick={openCreateBooking} disabled={studios.length === 0}>
              + New Booking
            </button>
          )}
        </div>

        {showCreateBooking && (
          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem', marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.75rem' }}>
              Booking for <strong>{customer.name}</strong>
              {(customer.phone || customer.whatsapp_number) && ` · ${customer.phone || customer.whatsapp_number}`}
              {customer.email && ` · ${customer.email}`}
            </div>
            {createError && <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}>{createError}</div>}
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
              <div className="form-group" style={{ margin: 0, flex: 1, minWidth: 160 }}>
                <label>Studio</label>
                <select value={createStudioId} onChange={e => handleCreateStudioChange(e.target.value)}>
                  {studios.map(s => (
                    <option key={s.id} value={s.id}>{s.name} ({s.country})</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ margin: 0, flex: 1, minWidth: 140 }}>
                <label>Date</label>
                <input
                  type="date"
                  value={createDate}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={e => handleCreateDateChange(e.target.value)}
                />
              </div>
              <div className="form-group" style={{ margin: 0, flex: 1, minWidth: 140 }}>
                <label>Time</label>
                <select value={createTime} onChange={e => setCreateTime(e.target.value)} disabled={createLoadingSlots || !createDate}>
                  <option value="">
                    {createLoadingSlots ? 'Loading...' : (createDate ? (createSlots.length ? 'Select time...' : 'No slots available') : 'Pick a date first')}
                  </option>
                  {createSlots.map(s => (
                    <option key={s.time} value={s.time}>{s.time}</option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleCreateBookingConfirm}
                disabled={!createStudioId || !createDate || !createTime || createSubmitting}
              >{createSubmitting ? 'Creating...' : 'Create Booking'}</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowCreateBooking(false)} disabled={createSubmitting}>Cancel</button>
            </div>
          </div>
        )}

        {bookings.length === 0 ? (
          <p style={{ color: '#999' }}>No upcoming bookings</p>
        ) : (
          bookings.map(b => (
            <div key={b.id} className="piercing-item" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                <div>
                  <strong>{new Date(b.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</strong>
                  <span style={{ marginLeft: 8, color: '#6b7280' }}>{b.time}</span>
                  <span style={{ marginLeft: 8, fontSize: '0.85rem', color: '#9ca3af' }}>{b.studio_name}</span>
                </div>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => openReschedule(b)}
                    disabled={actionLoading === b.id}
                  >Reschedule</button>
                  <button
                    className="btn btn-sm"
                    style={{ border: '1px solid #b32d2d', color: '#b32d2d', background: 'none' }}
                    onClick={() => handleCancelBooking(b.id)}
                    disabled={actionLoading === b.id}
                  >{actionLoading === b.id ? '...' : 'Cancel'}</button>
                </div>
              </div>

              {rescheduleBooking?.id === b.id && (
                <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem', width: '100%' }}>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                    <div className="form-group" style={{ margin: 0, flex: 1, minWidth: 140 }}>
                      <label>New Date</label>
                      <input
                        type="date"
                        value={rescheduleDate}
                        min={new Date().toISOString().split('T')[0]}
                        onChange={e => handleRescheduleDateChange(e.target.value)}
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0, flex: 1, minWidth: 140 }}>
                      <label>New Time</label>
                      <select value={rescheduleTime} onChange={e => setRescheduleTime(e.target.value)} disabled={rescheduleLoadingSlots || !rescheduleDate}>
                        <option value="">{rescheduleLoadingSlots ? 'Loading...' : 'Select time...'}</option>
                        {rescheduleSlots.map(s => (
                          <option key={s.time} value={s.time}>{s.time}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleRescheduleConfirm}
                      disabled={!rescheduleDate || !rescheduleTime || actionLoading === b.id}
                    >{actionLoading === b.id ? 'Saving...' : 'Confirm'}</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setRescheduleBooking(null)}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <div className="card">
        <h3>Visit History ({visits.length})</h3>
        {visits.length === 0 ? (
          <p style={{ color: '#999' }}>No visits recorded</p>
        ) : (
          visits.map((v) => {
            const hasDetail = (v.visit_type === 'checkup' && v.checkup)
              || (v.visit_type === 'new_piercing' && v.piercings?.length > 0)
              || (v.visit_type === 'downsizing' && v.downsized_by);
            const isExpanded = expandedVisits.has(v.id);
            // Who handled the visit — shown inline so it's visible without expanding
            const handledBy = v.visit_type === 'checkup' ? v.checkup?.piercer_name
              : v.visit_type === 'downsizing' ? v.downsized_by
              : null;
            return (
              <div key={v.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <div
                  className="piercing-item"
                  style={{ cursor: hasDetail ? 'pointer' : 'default', borderBottom: 'none' }}
                  onClick={() => hasDetail && toggleVisit(v.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span className={`tag ${visitTypeTag(v.visit_type)}`}>{visitTypeLabel(v.visit_type)}</span>
                    <span style={{ fontSize: 14 }}>{v.studio_name}</span>
                    {handledBy && <span style={{ fontSize: 12, color: '#999' }}>by {handledBy}</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 13, color: '#999' }}>{formatDateTime(v.checked_in_at)}</span>
                    {hasDetail && (
                      <span style={{ fontSize: 12, color: '#aaa', userSelect: 'none' }}>{isExpanded ? '▾' : '▸'}</span>
                    )}
                  </div>
                </div>
                {isExpanded && hasDetail && (
                  <div style={{ padding: '0.5rem 0 0.75rem 0.5rem', background: '#fafafa', borderRadius: 6, margin: '0 0 2px 0' }}>
                    {v.visit_type === 'checkup' && v.checkup && (
                      <div style={{ fontSize: 13 }}>
                        <span style={{ fontWeight: 600, color: '#555' }}>Issue: </span>
                        <span>{checkupIssueLabel(v.checkup.issue)}</span>
                        {v.checkup.piercer_name && <span style={{ color: '#999', marginLeft: 8 }}>checked by {v.checkup.piercer_name}</span>}
                        {v.checkup.notes && (
                          <div style={{ marginTop: 4, color: '#666' }}>{v.checkup.notes}</div>
                        )}
                      </div>
                    )}
                    {v.visit_type === 'downsizing' && v.downsized_by && (
                      <div style={{ fontSize: 13 }}>
                        <span style={{ fontWeight: 600, color: '#555' }}>Downsized</span>
                        <span style={{ color: '#999', marginLeft: 8 }}>by {v.downsized_by}</span>
                      </div>
                    )}
                    {v.visit_type === 'new_piercing' && v.piercings?.length > 0 && (
                      <div style={{ fontSize: 13 }}>
                        {v.piercings.map((p, i) => (
                          <div key={i} style={{ marginBottom: i < v.piercings.length - 1 ? 6 : 0 }}>
                            <span style={{ fontWeight: 600, color: '#555' }}>{placementLabelVisit(p.placement)}</span>
                            {p.piercer_name && <span style={{ color: '#999', marginLeft: 8 }}>by {p.piercer_name}</span>}
                            {p.notes && <div style={{ color: '#666', marginTop: 2 }}>{p.notes}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className="card">
        <h3>Piercings ({piercings.length})</h3>
        {piercings.length === 0 ? (
          <p style={{ color: '#999' }}>No piercings recorded</p>
        ) : (
          piercings.map((p) => (
            <div key={p.id} className="piercing-item">
              <div>
                <strong>{placementLabel(p.placement)}</strong>
                {p.notes && <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{p.notes}</div>}
                <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>Piercer: {p.piercer_name || '—'}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span className={`tag ${p.downsized ? 'tag-green' : 'tag-orange'}`}>
                  {p.downsized ? 'Downsized' : 'Awaiting Downsize'}
                </span>
                {p.downsized && p.downsized_date && (
                  <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>{formatDate(p.downsized_date)}</div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="card">
        <h3>Purchases ({purchases.length})</h3>
        {purchases.length === 0 ? (
          <p style={{ color: '#999' }}>No purchases synced from Shopify</p>
        ) : (
          purchases.map((p) => (
            <div key={p.id} className="piercing-item">
              <div>
                <strong>{p.shopify_order_id}</strong>
                <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
                  {Array.isArray(p.line_items) ? p.line_items.map(i => i.title || i.name).join(', ') : ''}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <strong>{p.currency} {p.total_amount}</strong>
                <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{formatDate(p.purchased_at)}</div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="card">
        <h3>Follow-ups</h3>
        {followUps.length === 0 ? (
          <p style={{ color: '#999' }}>No follow-ups scheduled</p>
        ) : (
          followUps.map((f) => (
            <div key={f.id} className="piercing-item">
              <div>
                <strong>{followUpLabel(f.trigger)}</strong>
                <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>Scheduled: {formatDateTime(f.scheduled_for)}</div>
              </div>
              <div>
                <span className={`tag ${statusTag(f.status)}`}>{f.status}</span>
                {f.sent_at && <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>Sent: {formatDateTime(f.sent_at)}</div>}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="card">
        <h3>Staff Notes ({notes.length})</h3>
        {notes.map((n) => (
          <div key={n.id} className="note-item">
            <div className="note-meta">{n.staff_name} — {formatDateTime(n.created_at)}</div>
            <div>{n.note}</div>
          </div>
        ))}
        <div className="note-form">
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Add a note..."
          />
          <button className="btn btn-primary" onClick={handleAddNote}>Add Note</button>
        </div>
      </div>
    </div>
  );
}

export default CustomerDetail;